import React, { useEffect, useState, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { Toaster } from 'sonner';
import { TenantBrandingProvider } from './contexts/TenantBrandingContext';
import FreezeScreen from './components/FreezeScreen';
import LicenseWarningBanner from './components/LicenseWarningBanner';
import api from './lib/api';

// EAGER pages: small, must show fast (login flow). Anything that needs to
// render BEFORE the user authenticates lives here.
import Login from './pages/Login';
import MemberLogin from './pages/MemberLogin';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ForceChangePassword from './pages/ForceChangePassword';
import MemberForceChangePassword from './pages/MemberForceChangePassword';
import SaasLogin from './saas/SaasLogin';

// LAZY pages: everything behind auth. Cuts the initial bundle by ~70%, so
// flaky mobile connections (TR cellular, observed Jun 2026) can actually
// finish downloading the login screen before the connection drops.
const Dashboard = lazy(() => import('./pages/Dashboard'));
const QuickSale = lazy(() => import('./pages/QuickSale'));
const Members = lazy(() => import('./pages/Members'));
const MemberDetail = lazy(() => import('./pages/MemberDetail'));
const Sales = lazy(() => import('./pages/Sales'));
const SaleDetail = lazy(() => import('./pages/SaleDetail'));
const Payments = lazy(() => import('./pages/Payments'));
const Expenses = lazy(() => import('./pages/Expenses'));
const Packages = lazy(() => import('./pages/Packages'));
const Users = lazy(() => import('./pages/Users'));
const Reports = lazy(() => import('./pages/Reports'));
const Calendar = lazy(() => import('./pages/Calendar'));
const Branches = lazy(() => import('./pages/Branches'));
const BranchView = lazy(() => import('./pages/BranchView'));
const AuditLogViewer = lazy(() => import('./pages/AuditLogViewer'));
const MemberPortal = lazy(() => import('./pages/MemberPortal'));
const Services = lazy(() => import('./pages/Services'));
const Lessons = lazy(() => import('./pages/Lessons'));
const LessonCalendar = lazy(() => import('./pages/LessonCalendar'));
const NotificationSettings = lazy(() => import('./pages/NotificationSettings'));
const NotificationCenter = lazy(() => import('./pages/NotificationCenter'));
const Backup = lazy(() => import('./pages/Backup'));
const BookingArchive = lazy(() => import('./pages/BookingArchive'));
const EmployeePerformance = lazy(() => import('./pages/EmployeePerformance'));
const QuickAccess = lazy(() => import('./pages/QuickAccess'));
const MonthlyReports = lazy(() => import('./pages/MonthlyReports'));
const Subscription = lazy(() => import('./pages/Subscription'));

// SaaS Admin: all lazy — tenant users never need these chunks.
const SaasDashboard = lazy(() => import('./saas/SaasDashboard'));
const TenantList = lazy(() => import('./saas/TenantList'));
const TenantDetail = lazy(() => import('./saas/TenantDetail'));
const TenantCreate = lazy(() => import('./saas/TenantCreate'));
const SaasSettings = lazy(() => import('./saas/SaasSettings'));
const BackupManager = lazy(() => import('./saas/BackupManager'));

/**
 * Shown while a lazy chunk is downloading. Intentionally pure-CSS so it
 * works even if the route's chunk is stuck/slow.
 */
const RouteFallback = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', color: '#6b0f1a', fontFamily: '-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif' }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{
        width: 28, height: 28, border: '3px solid #f1d6dc', borderTopColor: '#6b0f1a',
        borderRadius: '50%', margin: '0 auto 12px', animation: 'bsSpin 0.8s linear infinite'
      }} />
      <div style={{ fontSize: 13 }}>Yükleniyor…</div>
      <style>{`@keyframes bsSpin{to{transform:rotate(360deg)}}`}</style>
    </div>
  </div>
);

/**
 * Catches ChunkLoadError thrown by React.lazy when a code-split chunk fails
 * to download (truncated bundle on flaky cellular, expired hash after deploy,
 * etc.). Auto-retries ONCE with a cache-busting reload, then shows a manual
 * refresh button instead of looping forever.
 */
class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    try {
      if (window.__bs_report) {
        window.__bs_report({
          kind: 'react_error',
          message: error && error.message,
          stack: error && error.stack,
          name: error && error.name,
          componentStack: info && info.componentStack
        });
      }
      // Auto-retry once for ChunkLoadError only.
      var isChunk = error && (error.name === 'ChunkLoadError' || /Loading chunk|Loading CSS chunk|ChunkLoadError/i.test(error.message || ''));
      if (isChunk && sessionStorage.getItem('__bs_chunk_retried') !== '1') {
        sessionStorage.setItem('__bs_chunk_retried', '1');
        var u = new URL(window.location.href);
        u.searchParams.set('_r', String(Date.now()));
        window.location.replace(u.toString());
      }
    } catch (_) { /* ignore */ }
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: '-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif' }}>
          <div style={{ maxWidth: 420, textAlign: 'center' }}>
            <div style={{ color: '#6b0f1a', fontSize: 22, fontWeight: 700, marginBottom: 10 }}>Bir sorun oluştu</div>
            <p style={{ color: '#444', fontSize: 14, margin: '0 0 18px' }}>
              Sayfa yüklenirken bir dosya indirilemedi. Lütfen yenileyin.
            </p>
            <button onClick={() => { try { sessionStorage.removeItem('__bs_chunk_retried'); } catch (_) {} window.location.reload(); }}
                    style={{ background: '#6b0f1a', color: '#fff', border: 0, padding: '10px 22px', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              Yenile
            </button>
            <div style={{ marginTop: 14, fontSize: 11, color: '#888', fontFamily: 'SF Mono,Consolas,monospace' }}>
              {String(this.state.error && (this.state.error.name + ': ' + (this.state.error.message || '')))}
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Centralized Auth Guard - handles all auth logic in ONE place
const AuthGuard = ({ children, requireAuth = true, blockIfMustChangePassword = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  useEffect(() => {
    // If auth required but no token, redirect to login
    if (requireAuth && !token) {
      navigate('/login', { replace: true });
      return;
    }
    
    // If user must change password and not on force-change-password page
    if (requireAuth && token && user.must_change_password && blockIfMustChangePassword) {
      if (location.pathname !== '/force-change-password') {
        navigate('/force-change-password', { replace: true });
        return;
      }
    }
    
    // If user doesn't need to change password but is on force-change-password page
    if (token && !user.must_change_password && location.pathname === '/force-change-password') {
      navigate('/', { replace: true });
      return;
    }
  }, [token, user.must_change_password, location.pathname, navigate, requireAuth, blockIfMustChangePassword]);
  
  // If auth required and no token, don't render children
  if (requireAuth && !token) {
    return null;
  }
  
  // If must change password and trying to access other pages
  if (requireAuth && token && user.must_change_password && blockIfMustChangePassword && location.pathname !== '/force-change-password') {
    return null;
  }
  
  return <>{children}</>;
};

// Role-based route protection
const RoleGuard = ({ children, allowedRoles, fallback = '/' }) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  if (!allowedRoles.includes(user.role)) {
    return <Navigate to={fallback} replace />;
  }
  
  return <>{children}</>;
};

// Permission-aware route guard
const PermissionGuard = ({ children, allowedRoles = [], permissionKey = null, fallback = '/' }) => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  
  // If user's role is in allowed roles, allow access
  if (allowedRoles.includes(user.role)) {
    return <>{children}</>;
  }
  
  // If user has the specific permission, allow access
  if (permissionKey && user.permissions && user.permissions[permissionKey]) {
    return <>{children}</>;
  }
  
  return <Navigate to={fallback} replace />;
};
const MemberAuthGuard = ({ children, requireAuth = true, blockIfMustChangePassword = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const memberToken = localStorage.getItem('member_token');
  const member = JSON.parse(localStorage.getItem('member') || '{}');
  
  // Check auth status BEFORE rendering
  const shouldRedirectToLogin = requireAuth && !memberToken;
  const shouldRedirectToForceChange = requireAuth && memberToken && member.must_change_password && blockIfMustChangePassword && location.pathname !== '/member-force-change-password';
  const shouldRedirectToPortal = memberToken && !member.must_change_password && location.pathname === '/member-force-change-password';
  
  useEffect(() => {
    if (shouldRedirectToLogin) {
      navigate('/member-login', { replace: true });
    } else if (shouldRedirectToForceChange) {
      navigate('/member-force-change-password', { replace: true });
    } else if (shouldRedirectToPortal) {
      navigate('/member-portal', { replace: true });
    }
  }, [shouldRedirectToLogin, shouldRedirectToForceChange, shouldRedirectToPortal, navigate]);
  
  // Don't render children if we need to redirect
  if (shouldRedirectToLogin || shouldRedirectToForceChange || shouldRedirectToPortal) {
    return null;
  }
  
  return <>{children}</>;
};

function AppContent() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isEmployee = user.role === 'PERSONEL';
  const isGM = user.role === 'GENEL_MUDUR';
  const isBranchManager = user.role === 'SUBE_MUDURU';
  
  return (
    <ChunkErrorBoundary>
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/member-login" element={<MemberLogin />} />
      
      {/* Member Portal Routes */}
      <Route 
        path="/member-force-change-password" 
        element={
          <MemberAuthGuard requireAuth={true} blockIfMustChangePassword={false}>
            <MemberForceChangePassword />
          </MemberAuthGuard>
        } 
      />
      <Route 
        path="/member-portal" 
        element={
          <MemberAuthGuard requireAuth={true} blockIfMustChangePassword={true}>
            <MemberPortal />
          </MemberAuthGuard>
        } 
      />
      
      {/* Password Reset - Public Route (supports both /reset-password/:token and /reset-password?token=...) */}
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password/:token" element={<ResetPassword variant="staff" />} />
      <Route path="/reset-password" element={<ResetPassword variant="staff" />} />
      <Route path="/member-reset-password/:token" element={<ResetPassword variant="member" />} />
      <Route path="/member-reset-password" element={<ResetPassword variant="member" />} />
      
      {/* Force change password - special handling */}
      <Route 
        path="/force-change-password" 
        element={
          <AuthGuard requireAuth={true} blockIfMustChangePassword={false}>
            <ForceChangePassword />
          </AuthGuard>
        } 
      />
      
      {/* Dashboard - permission-guarded for employees */}
      <Route
        path="/"
        element={
          <AuthGuard>
            <PermissionGuard allowedRoles={['GENEL_MUDUR', 'SUBE_MUDURU', 'MUDUR_YARDIMCISI']} permissionKey="can_view_dashboard">
              <Dashboard />
            </PermissionGuard>
          </AuthGuard>
        }
      />
      
      {/* GM-only routes */}
      <Route
        path="/branches"
        element={
          <AuthGuard>
            <RoleGuard allowedRoles={['GENEL_MUDUR']}>
              <Branches />
            </RoleGuard>
          </AuthGuard>
        }
      />
      <Route
        path="/branches/:branchId"
        element={
          <AuthGuard>
            <RoleGuard allowedRoles={['GENEL_MUDUR']}>
              <BranchView />
            </RoleGuard>
          </AuthGuard>
        }
      />
      
      {/* Quick Sale - needs permission check */}
      <Route
        path="/quick-sale"
        element={
          <AuthGuard>
            <PermissionGuard allowedRoles={['GENEL_MUDUR', 'SUBE_MUDURU', 'MUDUR_YARDIMCISI']} permissionKey="can_view_quick_sale">
              <QuickSale />
            </PermissionGuard>
          </AuthGuard>
        }
      />
      
      {/* Members - Manager/GM + Employees with permission */}
      <Route
        path="/members"
        element={
          <AuthGuard>
            <PermissionGuard allowedRoles={['GENEL_MUDUR', 'SUBE_MUDURU', 'MUDUR_YARDIMCISI']} permissionKey="can_view_members">
              <Members />
            </PermissionGuard>
          </AuthGuard>
        }
      />
      <Route
        path="/members/:memberId"
        element={
          <AuthGuard>
            <PermissionGuard allowedRoles={['GENEL_MUDUR', 'SUBE_MUDURU', 'MUDUR_YARDIMCISI']} permissionKey="can_view_members">
              <MemberDetail />
            </PermissionGuard>
          </AuthGuard>
        }
      />
      
      {/* Sales - permission-guarded for employees */}
      <Route
        path="/sales"
        element={
          <AuthGuard>
            <PermissionGuard allowedRoles={['GENEL_MUDUR', 'SUBE_MUDURU', 'MUDUR_YARDIMCISI']} permissionKey="can_view_sales">
              <Sales />
            </PermissionGuard>
          </AuthGuard>
        }
      />
      <Route
        path="/sales/:saleId"
        element={
          <AuthGuard>
            <PermissionGuard allowedRoles={['GENEL_MUDUR', 'SUBE_MUDURU', 'MUDUR_YARDIMCISI']} permissionKey="can_view_sales">
              <SaleDetail />
            </PermissionGuard>
          </AuthGuard>
        }
      />
      
      {/* Payments - permission-guarded for employees */}
      <Route
        path="/payments"
        element={
          <AuthGuard>
            <PermissionGuard allowedRoles={['GENEL_MUDUR', 'SUBE_MUDURU', 'MUDUR_YARDIMCISI']} permissionKey="can_view_payments">
              <Payments />
            </PermissionGuard>
          </AuthGuard>
        }
      />
      
      {/* Expenses - Manager/GM + Employees with permission */}
      <Route
        path="/expenses"
        element={
          <AuthGuard>
            <PermissionGuard allowedRoles={['GENEL_MUDUR', 'SUBE_MUDURU', 'MUDUR_YARDIMCISI']} permissionKey="can_view_expenses">
              <Expenses />
            </PermissionGuard>
          </AuthGuard>
        }
      />
      
      {/* Packages - Manager/GM/Manager Assistant + Employees with permission */}
      <Route
        path="/packages"
        element={
          <AuthGuard>
            <PermissionGuard allowedRoles={['GENEL_MUDUR', 'SUBE_MUDURU', 'MUDUR_YARDIMCISI']} permissionKey="can_view_packages">
              <Packages />
            </PermissionGuard>
          </AuthGuard>
        }
      />
      
      {/* Services - Manager/GM/Manager Assistant + Employees with permission */}
      <Route
        path="/services"
        element={
          <AuthGuard>
            <PermissionGuard allowedRoles={['GENEL_MUDUR', 'SUBE_MUDURU', 'MUDUR_YARDIMCISI']} permissionKey="can_manage_services">
              <Services />
            </PermissionGuard>
          </AuthGuard>
        }
      />
      
      {/* Lessons - Manager/GM + Employees with permission (Phase 4) */}
      <Route
        path="/lessons"
        element={
          <AuthGuard>
            <PermissionGuard allowedRoles={['GENEL_MUDUR', 'SUBE_MUDURU', 'MUDUR_YARDIMCISI']} permissionKey="can_view_lessons">
              <Lessons />
            </PermissionGuard>
          </AuthGuard>
        }
      />
      
      {/* Users - Manager/GM only */}
      <Route
        path="/users"
        element={
          <AuthGuard>
            <RoleGuard allowedRoles={['GENEL_MUDUR', 'SUBE_MUDURU']}>
              <Users />
            </RoleGuard>
          </AuthGuard>
        }
      />
      
      {/* Reports - Manager/GM + Employees with permission */}
      <Route
        path="/reports"
        element={
          <AuthGuard>
            <PermissionGuard allowedRoles={['GENEL_MUDUR', 'SUBE_MUDURU']} permissionKey="can_view_reports">
              <Reports />
            </PermissionGuard>
          </AuthGuard>
        }
      />
      
      {/* Calendar - Manager/GM only */}
      <Route
        path="/calendar"
        element={
          <AuthGuard>
            <RoleGuard allowedRoles={['GENEL_MUDUR', 'SUBE_MUDURU']}>
              <Calendar />
            </RoleGuard>
          </AuthGuard>
        }
      />
      
      {/* Lesson Calendar - Manager/GM + Employees with permission */}
      <Route
        path="/lesson-calendar"
        element={
          <AuthGuard>
            <PermissionGuard allowedRoles={['GENEL_MUDUR', 'SUBE_MUDURU', 'MUDUR_YARDIMCISI']} permissionKey="can_view_calendar">
              <LessonCalendar />
            </PermissionGuard>
          </AuthGuard>
        }
      />
      
      {/* Audit Log - Manager/GM/Manager Assistant */}
      <Route
        path="/audit-log"
        element={
          <AuthGuard>
            <RoleGuard allowedRoles={['GENEL_MUDUR', 'SUBE_MUDURU', 'MUDUR_YARDIMCISI']}>
              <AuditLogViewer />
            </RoleGuard>
          </AuthGuard>
        }
      />

      {/* Notification Settings - Manager/GM/Manager Assistant */}
      <Route
        path="/notification-settings"
        element={
          <AuthGuard>
            <RoleGuard allowedRoles={['GENEL_MUDUR', 'SUBE_MUDURU', 'MUDUR_YARDIMCISI']}>
              <NotificationSettings />
            </RoleGuard>
          </AuthGuard>
        }
      />

      {/* Notification Center (channel-aware) - GM + Branch Manager */}
      <Route
        path="/notification-center"
        element={
          <AuthGuard>
            <RoleGuard allowedRoles={['GENEL_MUDUR', 'SUBE_MUDURU', 'MUDUR_YARDIMCISI']}>
              <NotificationCenter />
            </RoleGuard>
          </AuthGuard>
        }
      />
      
      {/* Backup & Restore - GM and Branch Managers */}
      <Route
        path="/backup"
        element={
          <AuthGuard>
            <RoleGuard allowedRoles={['GENEL_MUDUR', 'SUBE_MUDURU']}>
              <Backup />
            </RoleGuard>
          </AuthGuard>
        }
      />

      {/* Booking Activity Archive - GM, BM, MY, and employees with permission */}
      <Route
        path="/booking-archive"
        element={
          <AuthGuard>
            <PermissionGuard allowedRoles={['GENEL_MUDUR', 'SUBE_MUDURU', 'MUDUR_YARDIMCISI']} permissionKey="can_view_booking_archive">
              <BookingArchive />
            </PermissionGuard>
          </AuthGuard>
        }
      />

      {/* Employee Performance - GM and Branch Managers */}
      <Route
        path="/employee-performance"
        element={
          <AuthGuard>
            <RoleGuard allowedRoles={['GENEL_MUDUR', 'SUBE_MUDURU']}>
              <EmployeePerformance />
            </RoleGuard>
          </AuthGuard>
        }
      />

      {/* Quick Access - Branch Managers and Employees */}
      <Route
        path="/home"
        element={
          <AuthGuard>
            <QuickAccess />
          </AuthGuard>
        }
      />

      {/* Monthly Reports - GM and Branch Managers */}
      <Route
        path="/monthly-reports"
        element={
          <AuthGuard>
            <RoleGuard allowedRoles={['GENEL_MUDUR', 'SUBE_MUDURU']}>
              <MonthlyReports />
            </RoleGuard>
          </AuthGuard>
        }
      />

      {/* Subscription - GM and Branch Managers */}
      <Route
        path="/subscription"
        element={
          <AuthGuard>
            <RoleGuard allowedRoles={['GENEL_MUDUR', 'SUBE_MUDURU']}>
              <Subscription />
            </RoleGuard>
          </AuthGuard>
        }
      />

      {/* SaaS Admin Routes */}
      <Route path="/saas-admin" element={<SaasLogin />} />
      <Route path="/saas-admin/dashboard" element={<SaasDashboard />} />
      <Route path="/saas-admin/tenants" element={<TenantList />} />
      <Route path="/saas-admin/tenants/new" element={<TenantCreate />} />
      <Route path="/saas-admin/tenants/:id" element={<TenantDetail />} />
      <Route path="/saas-admin/settings" element={<SaasSettings />} />
      <Route path="/saas-admin/backups" element={<BackupManager />} />
    </Routes>
    </Suspense>
    </ChunkErrorBoundary>
  );
}

// Tenant status gate: checks subscription status and shows freeze screen or warning
function TenantStatusGate({ children }) {
  const [tenantStatus, setTenantStatus] = useState(null); // null = loading
  const [frozen, setFrozen] = useState(false);
  const [frozenReason, setFrozenReason] = useState(null);
  const [frozenStatus, setFrozenStatus] = useState('frozen');
  const [licenseWarning, setLicenseWarning] = useState(null);
  const location = useLocation();

  // Skip status check for SaaS admin pages
  const isSaasAdmin = location.pathname.startsWith('/saas-admin');

  const checkStatus = useCallback(async () => {
    if (isSaasAdmin) return;
    try {
      const res = await api.get('/api/tenant/status');
      const data = res.data;
      setTenantStatus(data.status);
      if (data.status === 'frozen' || data.status === 'expired') {
        setFrozen(true);
        setFrozenStatus(data.status);
        setFrozenReason(data.frozen_reason);
      } else {
        setFrozen(false);
        if (data.warning) {
          setLicenseWarning({ days_remaining: data.days_remaining });
        }
      }
    } catch (err) {
      // If we get a 403 with frozen flag, it means frozen
      if (err.response?.status === 403 && err.response?.data?.frozen) {
        setFrozen(true);
        setFrozenStatus(err.response.data.tenant_status || 'frozen');
      }
    }
  }, [isSaasAdmin]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  // Listen for tenant-frozen events dispatched by the API interceptor
  useEffect(() => {
    const handler = (e) => {
      setFrozen(true);
      setFrozenStatus(e.detail?.status || 'frozen');
    };
    window.addEventListener('tenant-frozen', handler);
    return () => window.removeEventListener('tenant-frozen', handler);
  }, []);

  // SaaS admin pages bypass status check entirely
  if (isSaasAdmin) return <>{children}</>;

  // Show freeze screen
  if (frozen) {
    return <FreezeScreen status={frozenStatus} frozenReason={frozenReason} />;
  }

  return (
    <>
      {licenseWarning && (
        <LicenseWarningBanner
          daysRemaining={licenseWarning.days_remaining}
          onDismiss={() => setLicenseWarning(null)}
        />
      )}
      {children}
    </>
  );
}

// Detect if we're on the admin subdomain
function isAdminHost() {
  const host = window.location.hostname;
  return host.startsWith('admin.');
}

// On admin host: redirect root to SaaS admin, block tenant routes
function AdminHostGate({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const onAdminHost = isAdminHost();

  useEffect(() => {
    if (!onAdminHost) return;
    // If on admin host but NOT on a /saas-admin path, redirect to SaaS admin
    if (!location.pathname.startsWith('/saas-admin')) {
      const saasToken = localStorage.getItem('saas_token');
      navigate(saasToken ? '/saas-admin/dashboard' : '/saas-admin', { replace: true });
    }
  }, [onAdminHost, location.pathname, navigate]);

  if (onAdminHost && !location.pathname.startsWith('/saas-admin')) {
    return null; // Don't render anything while redirecting
  }

  return <>{children}</>;
}

function App() {
  try { if (window.__bs_report) window.__bs_report({ kind: 'APP_COMPONENT_STARTED' }); } catch (_) {}
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <TenantBrandingProvider>
          <AdminHostGate>
            <TenantStatusGate>
              <AppContent />
            </TenantStatusGate>
          </AdminHostGate>
        </TenantBrandingProvider>
        <Toaster position="top-center" richColors />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
